import { useEffect, useMemo, useRef, useState } from 'react';
import type { DistinctValue } from '@/cache/ResultCache';
import { formatCount } from '@/lib/format';
import { Spinner } from '@/components/common/states';
import { Popover } from '@/components/common/Popover';

/**
 * Value editor popup: a text input plus a Suggested Values list of distinct
 * values from the cached table.
 *
 * The suggestions are the reason this is worth building. Navicat round-trips to
 * the database to populate that list; against the DuckDB-WASM cache it is a
 * local `SELECT DISTINCT` and returns in single-digit milliseconds. When there
 * is no cache we hide the list and say so rather than firing a server query.
 */

export interface ValueEditorProps {
  readonly column: string;
  /** Multi-value operators (`in`) keep the whole list; others take one value. */
  readonly multiple: boolean;
  readonly initial: readonly string[];
  readonly loadSuggestions: () => Promise<readonly DistinctValue[] | null>;
  readonly onCommit: (values: readonly string[]) => void;
  readonly onCancel: () => void;
  /** The control this popup hangs off. */
  readonly anchorRef: React.RefObject<HTMLElement | null>;
}

export function ValueEditor({
  column,
  multiple,
  initial,
  loadSuggestions,
  onCommit,
  onCancel,
  anchorRef,
}: ValueEditorProps): JSX.Element {
  const [text, setText] = useState(initial.join(multiple ? '\n' : ''));
  const [search, setSearch] = useState('');
  const [suggestions, setSuggestions] = useState<readonly DistinctValue[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadSuggestions()
      .then((values) => {
        if (cancelled) return;
        setSuggestions(values);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setFailed(true);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loadSuggestions]);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const selected = useMemo(
    () => new Set(text.split('\n').map((line) => line.trim()).filter((line) => line !== '')),
    [text],
  );

  const visible = useMemo(() => {
    if (!suggestions) return [];
    const needle = search.trim().toLowerCase();
    if (needle === '') return suggestions;
    return suggestions.filter((entry) =>
      (entry.isNull ? 'null' : (entry.value ?? '')).toLowerCase().includes(needle),
    );
  }, [suggestions, search]);

  const toggle = (entry: DistinctValue): void => {
    const literal = entry.isNull ? 'NULL' : (entry.value ?? '');
    if (multiple) {
      const next = new Set(selected);
      if (next.has(literal)) next.delete(literal);
      else next.add(literal);
      setText([...next].join('\n'));
    } else {
      setText(literal);
    }
  };

  const commit = (): void => {
    const values = multiple
      ? text.split('\n').map((line) => line.trim()).filter((line) => line !== '')
      : [text];
    onCommit(values);
  };

  return (
    <Popover
      anchorRef={anchorRef}
      onClose={onCancel}
      width={288}
      role="dialog"
      ariaLabel={`Value for ${column}`}
      testId="value-editor"
    >
    <div
      className="flex min-h-0 flex-col"
      onKeyDown={(event) => {
        if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
          event.preventDefault();
          commit();
        }
      }}
    >
      <div className="border-b border-line p-1.5">
        <textarea
          ref={inputRef}
          className="w-full resize-y rounded-sm border border-line bg-base px-2 py-1 font-mono text-xs text-ink outline-none focus:border-local"
          rows={multiple ? 3 : 1}
          aria-label={multiple ? `Values for ${column}, one per line` : `Value for ${column}`}
          placeholder={multiple ? 'one value per line' : 'value'}
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
      </div>

      <div className="flex items-center justify-between gap-2 px-2 pt-1.5">
        <span className="eyebrow">Suggested values</span>
        {suggestions ? (
          <span className="tnum text-[10px] text-faint">{suggestions.length}</span>
        ) : null}
      </div>

      {loading ? (
        <div className="p-3">
          <Spinner label="Reading distinct values" />
        </div>
      ) : failed || suggestions === null ? (
        <p className="px-2 py-2 text-[10px] leading-4 text-remote">
          {failed
            ? 'Could not read distinct values from the cache.'
            : 'Suggestions need the in-browser cache. This result is served from the server, so distinct values are not listed here.'}
        </p>
      ) : (
        <>
          <div className="px-1.5 pb-1.5 pt-1">
            <input
              className="field"
              type="search"
              placeholder="Search values"
              aria-label="Search suggested values"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <div className="min-h-0 flex-1 overflow-auto border-t border-line" role="group" aria-label="Suggested values">
            {visible.map((entry, index) => {
              const literal = entry.isNull ? 'NULL' : (entry.value ?? '');
              const isChecked = selected.has(literal);
              return (
                <label
                  key={`${literal}-${index}`}
                  className="flex cursor-pointer items-center gap-2 px-2 py-1 transition-colors hover:bg-raised"
                >
                  <input
                    type={multiple ? 'checkbox' : 'radio'}
                    checked={isChecked}
                    onChange={() => toggle(entry)}
                    aria-label={entry.isNull ? 'NULL' : entry.value === '' ? 'empty string' : literal}
                  />
                  <span
                    className={`min-w-0 flex-1 truncate font-mono text-[11px] ${
                      entry.isNull ? 'v-null' : entry.value === '' ? 'v-empty' : ''
                    }`}
                  >
                    {entry.isNull ? 'NULL' : entry.value === '' ? '""' : entry.value}
                  </span>
                  <span className="tnum shrink-0 text-[10px] text-faint">
                    {formatCount(entry.count)}
                  </span>
                </label>
              );
            })}
            {visible.length === 0 ? (
              <p className="px-2 py-3 text-center text-[11px] text-muted">No value matches.</p>
            ) : null}
          </div>
        </>
      )}

      <div className="flex justify-end gap-1 border-t border-line p-1.5">
        <button type="button" className="btn" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="btn btn-run" onClick={commit}>
          OK
        </button>
      </div>
    </div>
    </Popover>
  );
}
